/**
 * HumanHandoffPage — DIGEMP-HUMAN-HANDOFF-FRONTEND-058, route /handoffs.
 *
 * The queue of work Digital Employees stopped and handed to a human: finished-and-waiting-for-
 * review, waiting-for-approval, blocked on the customer, and paused. Each row carries a one-line
 * employee statement so the technician can triage WITHOUT opening anything, and links to the
 * packaged briefing (HandoffBriefing) that replaces rereading the conversation or the run log.
 *
 * Grouping/fetching is by run status only — read-only against the EXISTING agent-runs list
 * endpoint (same agents_view gate every other agent surface uses). A group whose fetch failed is
 * shown as "not loaded" rather than rendered as an empty (i.e. "nothing waiting") list.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { agentRunsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar from "../components/EmployeeAvatar";
import { HANDOFF_GROUPS, handoffStage, waitingHebrew, handoffSummary } from "../components/HandoffBriefing";
import { degradedAnalysisLabel } from "../components/degradedAnalysisVocabulary";

const GROUP_LIMIT = 50;

const FILTERS = [{ kind: "", label: "הכל" }, ...HANDOFF_GROUPS.map((g) => ({ kind: g.kind, label: g.title }))];

function byOldestWaiting(a, b) {
  const ta = a.updated_at ? new Date(a.updated_at).getTime() : Infinity;
  const tb = b.updated_at ? new Date(b.updated_at).getTime() : Infinity;
  return ta - tb;
}

function HandoffRow({ run }) {
  const stage = handoffStage(run.status);
  const { text: summary, degraded } = handoffSummary(run);
  // On the pilot default (no ANALYSIS_PROVIDER) the line below is the fallback adapter's scaffolding
  // — the ticket's own title restated as a finding — so it carries the shared vocabulary's label,
  // ahead of the words it qualifies. The briefing one click away already says this; a row that lets a
  // technician triage without opening anything has to say it too.
  const degradedLabel = degradedAnalysisLabel(degraded);
  const waiting = waitingHebrew(run.updated_at);
  return (
    <li data-testid="handoff-row" className="px-4 py-3 flex flex-wrap items-start gap-3">
      <EmployeeAvatar name={run.agent_name || "עובד דיגיטלי"} size="sm" />
      <div className="flex-1 min-w-[220px] space-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-gray-800">{run.agent_name || "עובד דיגיטלי"}</span>
          <span className="text-gray-400 text-xs">
            {run.ticket_number != null ? `קריאה #${run.ticket_number}` : "קריאה"}
            {run.ticket_title ? ` — ${run.ticket_title}` : ""}
          </span>
          {run.ticket_company_name && <span className="text-gray-400 text-xs">· {run.ticket_company_name}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {stage && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">{stage.label}</span>}
          <span className="text-gray-400">
            {waiting ? `ממתין לאדם ${waiting}` : "זמן ההמתנה לא ידוע"}
          </span>
        </div>
        {degradedLabel && (
          <span
            data-testid="handoff-degraded"
            className="inline-block text-[10px] font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
          >{degradedLabel}</span>
        )}
        {summary ? (
          <p className="text-xs text-gray-600 leading-relaxed">{summary.length > 180 ? `${summary.slice(0, 180)}…` : summary}</p>
        ) : (
          <p className="text-xs text-gray-400 italic">העובד לא סיפק סיכום — פתח את התדריך לפרטים</p>
        )}
        {stage && <p className="text-xs text-gray-500">נדרש: {stage.required}</p>}
      </div>
      <div className="flex flex-col items-start gap-1 text-xs shrink-0">
        <Link
          to={`/handoffs/${run.id}`}
          className="px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          תדריך מסירה
        </Link>
        {run.ticket_id && (
          <Link to={`/tickets/${run.ticket_id}`} className="text-blue-600 hover:underline">
            פתח קריאה
          </Link>
        )}
      </div>
    </li>
  );
}

export default function HumanHandoffPage() {
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");
  const [kindFilter, setKindFilter] = useState("");

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["human-handoffs"],
    queryFn: async () => {
      const results = await Promise.allSettled(
        HANDOFF_GROUPS.flatMap((g) => g.statuses.map((status) => agentRunsApi.listAll({ status, limit: GROUP_LIMIT })))
      );
      let i = 0;
      return HANDOFF_GROUPS.map((group) => {
        const runs = [];
        let loaded = true;
        group.statuses.forEach(() => {
          const res = results[i++];
          if (res.status === "fulfilled") runs.push(...(res.value || []));
          else loaded = false;
        });
        runs.sort(byOldestWaiting);
        return { ...group, runs, loaded };
      });
    },
    enabled: canView,
    refetchInterval: 30000,
  });

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במסירה לאדם</div>;
  }

  const groups = data || [];
  const visibleGroups = kindFilter ? groups.filter((g) => g.kind === kindFilter) : groups;
  const totalWaiting = groups.reduce((sum, g) => (g.loaded ? sum + g.runs.length : sum), 0);
  const anyFailed = groups.some((g) => !g.loaded);

  return (
    <div className="w-full max-w-[1200px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-800">מסירה לאדם</h1>
          <p className="text-xs text-gray-400">
            עבודות שעובדים דיגיטליים העבירו לטיפול אנושי — כולל תדריך מוכן לכל מסירה
          </p>
        </div>
        <div className="text-sm text-gray-500">
          {isLoading ? "טוען..." : anyFailed ? "ממתינות (טעינה חלקית)" : `${totalWaiting} ממתינות`}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.kind}
            type="button"
            onClick={() => setKindFilter(f.kind)}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              kindFilter === f.kind
                ? "bg-gray-800 text-white border-gray-800"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          שגיאה בטעינת המסירות
          <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
        </div>
      )}

      {!isLoading && !isError && totalWaiting === 0 && !anyFailed && (
        <p className="text-gray-400 text-sm italic">אין מסירות ממתינות כרגע</p>
      )}

      {visibleGroups.map((group) => {
        if (!group.loaded && group.runs.length === 0) {
          return (
            <div
              key={group.kind}
              data-testid={`handoff-group-${group.kind}-failed`}
              className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2"
            >
              לא ניתן היה לטעון את קבוצת „{group.title}” — ייתכן שיש מסירות נוספות שאינן מוצגות.
              <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
            </div>
          );
        }
        if (group.runs.length === 0) return null;
        return (
          <section key={group.kind} data-testid={`handoff-group-${group.kind}`} className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-700">{group.title}</h2>
              <span className="text-xs text-gray-500">{group.runs.length}</span>
            </div>
            {!group.loaded && (
              <p data-testid={`handoff-group-${group.kind}-partial`} className="px-4 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
                טעינה חלקית — חלק מהמסירות בקבוצה הזו לא נטענו.
              </p>
            )}
            <ul className="divide-y divide-gray-100">
              {group.runs.map((run) => <HandoffRow key={run.id} run={run} />)}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
